import React from "react";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export type CalloutVariant = "info" | "warning" | "error" | "success";

interface CalloutProps {
  variant?: CalloutVariant;
  title?: string;
  children?: React.ReactNode;
  /** A control at the far end, e.g. a Retry button. */
  action?: React.ReactNode;
  className?: string;
}

const VARIANT_TONE: Record<CalloutVariant, string> = {
  info: "border-accent/40 bg-accent/8 text-accent",
  warning: "border-warning/40 bg-warning/5 text-warning",
  error: "border-error/40 bg-error/5 text-error",
  success: "border-success/40 bg-success/5 text-success",
};

const VARIANT_ICON: Record<CalloutVariant, React.ReactNode> = {
  info: <InfoOutlinedIcon className="icon-md shrink-0 mt-0.5" />,
  warning: <WarningAmberIcon className="icon-md shrink-0 mt-0.5" />,
  error: <ErrorIcon className="icon-md shrink-0 mt-0.5" />,
  success: <CheckCircleIcon className="icon-md shrink-0 mt-0.5" />,
};

/** Inline status banner: icon + optional title + body. Tokens only. */
export const Callout: React.FC<CalloutProps> = ({
  variant = "info",
  title,
  children,
  action,
  className = "",
}) => (
  <div
    role="note"
    className={`flex items-start gap-2 p-3 border rounded-app ${VARIANT_TONE[variant]} ${className}`}
  >
    {VARIANT_ICON[variant]}
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      {title && <span className="text-fg-medium">{title}</span>}
      {children && <span className="text-fg-regular">{children}</span>}
    </div>
    {action && <span className="ml-auto shrink-0">{action}</span>}
  </div>
);
